import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { errorMessage } from "../lib/api";
import { pluralise } from "../lib/format";
import { useLogout, useUpdateMe } from "../lib/queries";
import type { Me } from "../lib/types";
import { useToasts } from "./Toasts";

export type UserMenuProps = {
  me: Me;
};

/**
 * The reader's own details, and the way out.
 *
 * A native `details`/`summary` disclosure: keyboard-operable and screen-reader
 * friendly without hand-rolling a menu widget.
 */
export function UserMenu({ me }: UserMenuProps) {
  const navigate = useNavigate();
  const { push } = useToasts();
  const logout = useLogout();
  const details = useRef<HTMLDetailsElement | null>(null);

  // Click outside, or Escape, closes it.
  useEffect(() => {
    const node = details.current;
    if (!node) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!node.open) return;
      if (event.target instanceof Node && node.contains(event.target)) return;
      node.open = false;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && node.open) {
        node.open = false;
        node.querySelector("summary")?.focus();
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const signOut = () => {
    logout.mutate(undefined, {
      onSuccess: () => {
        if (details.current) details.current.open = false;
        void navigate("/", { replace: true });
      },
      onError: (error) =>
        push({ kind: "problem", title: "Couldn't sign you out.", body: errorMessage(error) }),
    });
  };

  const initial = (me.givenName ?? me.displayName ?? me.email).trim().charAt(0).toUpperCase();

  return (
    <details ref={details} className="relative">
      <summary
        className="touch flex cursor-pointer list-none items-center gap-2 rounded-full py-1 pr-2 pl-1 transition-colors hover:bg-paper-deep [&::-webkit-details-marker]:hidden"
        aria-label={`Account — ${me.displayName}`}
      >
        <Avatar url={me.avatarUrl} initial={initial} name={me.displayName} />
        <span className="hidden font-label text-sm text-ink sm:inline">{me.givenName ?? me.displayName}</span>
      </summary>

      <div className="sheet grain absolute right-0 z-50 mt-2 w-64 p-4 shadow-lifted">
        <p className="display-6 text-lg leading-tight text-ink">{me.displayName}</p>
        <p className="mt-0.5 truncate font-prose text-sm text-ink-soft">{me.email}</p>

        <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-rule pt-3">
          <Stat label="Finds" value={me.stats.entries} />
          <Stat label="Collections" value={me.stats.collections} />
          <Stat label="Kingdoms" value={me.stats.domains} />
          <Stat label="Ticked off" value={me.stats.quarryFound} />
        </dl>

        <p className="mt-3 font-prose text-xs text-ink-soft">
          Keeping this book since {new Date(me.createdAt).getFullYear()} ·{" "}
          {pluralise(me.stats.entries, "page")}
        </p>

        <DefaultPlaceField me={me} />

        <button
          type="button"
          onClick={signOut}
          disabled={logout.isPending}
          className="touch mt-4 w-full border border-rule px-3 py-2 font-label text-sm tracking-wide text-ink transition-colors hover:bg-paper-deep disabled:opacity-60"
        >
          {logout.isPending ? "Signing out…" : "Sign out"}
        </button>
      </div>
    </details>
  );
}

/**
 * Where your finds usually happen. Applied to a new entry only when its photo
 * carries no location of its own — so this is what lets classification reason
 * about a region even when EXIF was stripped or never existed.
 */
function DefaultPlaceField({ me }: { me: Me }) {
  const id = useId();
  const { push } = useToasts();
  const update = useUpdateMe();
  const current = me.defaultPlace?.label ?? null;
  const [editing, setEditing] = useState(current === null);
  const [draft, setDraft] = useState(current ?? "");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const query = draft.trim();
    if (query.length === 0 || query === current) {
      setEditing(false);
      return;
    }
    update.mutate(
      { placeQuery: query },
      {
        onSuccess: () => setEditing(false),
        onError: (error) =>
          push({
            kind: "problem",
            title: "That place didn't resolve.",
            body: errorMessage(error, "Try a town and a region, like “Madison, Wisconsin”."),
          }),
      },
    );
  };

  const clear = () => {
    update.mutate(
      { placeQuery: "" },
      { onSuccess: () => setDraft(""), onError: (error) => push({ kind: "problem", title: "Couldn't clear it.", body: errorMessage(error) }) },
    );
  };

  return (
    <div className="mt-3 border-t border-rule pt-3">
      <p className="field-label-tight text-ink-soft">Default location</p>
      {editing ? (
        <form onSubmit={submit} className="mt-1.5 flex items-stretch gap-1.5">
          <label htmlFor={id} className="sr-only">
            Default location
          </label>
          <input
            id={id}
            type="text"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Madison, Wisconsin"
            className="min-w-0 flex-1 border border-rule bg-paper px-2 py-1 font-prose text-sm text-ink placeholder:text-ink-soft/60 focus:border-ochre focus:outline-none"
          />
          <button
            type="submit"
            disabled={update.isPending || draft.trim().length === 0}
            className="touch shrink-0 border border-ink bg-ink px-2 py-1 font-label text-xs text-paper disabled:opacity-40"
          >
            Set
          </button>
        </form>
      ) : (
        <div className="mt-1 flex items-center justify-between gap-2">
          <p className="truncate font-prose text-sm text-ink">{current}</p>
          <div className="flex shrink-0 items-center gap-2">
            <button type="button" onClick={() => setEditing(true)} className="link font-label text-xs">
              Change
            </button>
            <button
              type="button"
              onClick={clear}
              disabled={update.isPending}
              className="font-label text-xs text-ink-soft underline decoration-rule underline-offset-4 hover:text-ink disabled:opacity-60"
            >
              Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <dt className="field-label-tight text-ink-soft">{label}</dt>
      <dd className="display-6 text-xl leading-none text-ink">{value}</dd>
    </div>
  );
}

function Avatar({ url, initial, name }: { url: string | null; initial: string; name: string }) {
  if (url) {
    return (
      <img
        src={url}
        alt=""
        width={32}
        height={32}
        referrerPolicy="no-referrer"
        className="size-8 shrink-0 rounded-full border border-rule object-cover"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      title={name}
      className="flex size-8 shrink-0 items-center justify-center rounded-full border border-rule bg-paper-deep display-6 text-sm text-ink"
    >
      {initial}
    </span>
  );
}
